<template>
  <div
    ref="editorContainer"
    class="formula_input"
    :class="{ formula_input_empty: isEmpty }"
    :data-placeholder="isEmpty ? placeholder : ''"
  />
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, nextTick } from "vue";
import {
  registerFormulaBlots,
  setupFormulaKeyboardHandlers,
  setupFormulaClickHandlers,
  setupFormulaMutationObserver,
  buildFormulaString,
  parseFormulaString,
  getOperatorLabel,
} from "@/src/utils/formulaQuillUtils.js";
import "quill/dist/quill.snow.css";
import Quill from "quill";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  numberAttributes: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: "Type numbers or select properties and functions below",
  },
});

const emit = defineEmits(["update:modelValue"]);

const editorContainer = ref(null);
const isEmpty = ref(true);
let quill = null;
let observer = null;
let isInternalChange = false;
let lastCursorPosition = null;

registerFormulaBlots();

const getAttributeById = (id) => {
  try {
    return props.numberAttributes.find((a) => String(a.id) === String(id));
  } catch (error) {
    console.error("An error occurred while getting attribute by id:", error);
    return null;
  }
};

const getAttributeName = (id) => {
  try {
    const attr = getAttributeById(id);
    return attr?.name || id;
  } catch (error) {
    console.error("An error occurred while getting attribute name:", error);
    return id;
  }
};

const initQuill = () => {
  try {
    if (!editorContainer.value) return;

    quill = new Quill(editorContainer.value, {
      theme: null,
      placeholder: "",
      modules: {
        toolbar: false,
        clipboard: {
          matchVisual: false,
        },
      },
    });

    setupFormulaKeyboardHandlers(quill);
    setupFormulaClickHandlers(quill);
    observer = setupFormulaMutationObserver(quill);

    quill.on("text-change", () => {
      try {
        if (isInternalChange) {
          return;
        }
        updateEmptyState();
        emitFormula();
      } catch (error) {
        console.error("An error occurred during text change:", error);
      }
    });

    quill.root.addEventListener("paste", (e) => {
      try {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        const cleanText = text.replace(/[^0-9.\-\s]/g, "");
        if (cleanText) {
          const selection = quill.getSelection();
          if (selection) {
            quill.insertText(selection.index, cleanText);
          }
        }
      } catch (error) {
        console.error("An error occurred during paste:", error);
      }
    });

    if (props.modelValue) {
      nextTick(() => {
        renderFormulaToEditor();
      });
    }

    updateEmptyState();
  } catch (error) {
    console.error("An error occurred while initializing Quill:", error);
  }
};

const updateEmptyState = () => {
  try {
    if (!quill) return;
    const text = quill.getText().trim();
    const hasBlots =
      quill.root.querySelectorAll(
        ".formula_operator, .formula_property, .formula_keyword, .formula_comparison, .formula_string",
      ).length > 0;
    isEmpty.value = !text && !hasBlots;
  } catch (error) {
    console.error("An error occurred while updating empty state:", error);
    isEmpty.value = true;
  }
};

const emitFormula = () => {
  try {
    if (!quill) return;
    const formula = buildFormulaString(quill);
    emit("update:modelValue", formula || "");
  } catch (error) {
    console.error("An error occurred while emitting formula:", error);
  }
};

const renderFormulaToEditor = () => {
  try {
    if (!quill || !props.modelValue) {
      return;
    }

    isInternalChange = true;
    quill.deleteText(0, quill.getLength());

    const elements = parseFormulaString(props.modelValue, getAttributeById);
    renderElements(elements);

    updateEmptyState();

    nextTick(() => {
      isInternalChange = false;
    });
  } catch (error) {
    console.error(
      "An error occurred while rendering formula to editor:",
      error,
    );
    isInternalChange = false;
  }
};

const renderElements = (elements) => {
  try {
    let index = 0;

    const isNextClosingBracket = (currentIndex) => {
      const next = elements[currentIndex + 1];
      return next?.type === "bracket" && next?.bracket === ")";
    };

    elements.forEach((el, elIndex) => {
      try {
        if (el.type === "attribute") {
          const name = getAttributeName(el.id);
          quill.insertEmbed(index, "formula-property", {
            id: el.id,
            name: name,
          });
          index += 1;
          if (!isNextClosingBracket(elIndex)) {
            quill.insertText(index, " ");
            index += 1;
          }
        } else if (el.type === "operator") {
          quill.insertEmbed(index, "formula-operator", {
            operator: el.operator,
            label: getOperatorLabel(el.operator),
          });
          index += 1;
          quill.insertText(index, " ");
          index += 1;
        } else if (el.type === "aggregation") {
          const label = getOperatorLabel(el.operator);
          const labelWithoutBrackets = label
            .replace(/\s*\(\)$/, "")
            .replace(/\s*\((\w+)\)$/, " $1");
          quill.insertEmbed(index, "formula-operator", {
            operator: el.operator,
            label: labelWithoutBrackets.trim(),
          });
          index += 1;
          quill.insertText(index, " ");
          index += 1;
        } else if (el.type === "keyword") {
          quill.insertEmbed(index, "formula-keyword", {
            keyword: el.keyword,
          });
          index += 1;
          quill.insertText(index, " ");
          index += 1;
        } else if (el.type === "comparison") {
          quill.insertEmbed(index, "formula-comparison", {
            operator: el.operator,
          });
          index += 1;
          quill.insertText(index, " ");
          index += 1;
        } else if (el.type === "string") {
          quill.insertEmbed(index, "formula-string", {
            value: el.value,
          });
          index += 1;
          if (!isNextClosingBracket(elIndex)) {
            quill.insertText(index, " ");
            index += 1;
          }
        } else if (el.type === "literal") {
          const text = String(el.value);
          quill.insertText(index, text);
          index += text.length;
          if (!isNextClosingBracket(elIndex)) {
            quill.insertText(index, " ");
            index += 1;
          }
        } else if (el.type === "bracket") {
          quill.insertText(index, el.bracket);
          index += 1;
          if (el.bracket === ")") {
            quill.insertText(index, " ");
            index += 1;
          }
        } else if (el.type === "comma") {
          quill.insertText(index, ", ");
          index += 2;
        }
      } catch (elementError) {
        console.error(
          "An error occurred while rendering element:",
          elementError,
        );
      }
    });
  } catch (error) {
    console.error("An error occurred while rendering elements:", error);
  }
};

const insertProperty = (id, name) => {
  try {
    if (!quill) return;

    isInternalChange = true;
    quill.focus();

    const selection = quill.getSelection();
    const index =
      lastCursorPosition !== null
        ? lastCursorPosition
        : selection
          ? selection.index
          : quill.getLength() - 1;
    lastCursorPosition = null;

    quill.insertEmbed(index, "formula-property", {
      id: id,
      name: name,
    });

    quill.insertText(index + 1, " ");
    quill.setSelection(index + 2, 0);

    isInternalChange = false;
    updateEmptyState();
    emitFormula();
  } catch (error) {
    console.error("An error occurred while inserting property:", error);
    isInternalChange = false;
  }
};

const insertOperator = (operator) => {
  try {
    if (!quill) {
      return;
    }

    isInternalChange = true;

    const selection = quill.getSelection();
    const index = selection ? selection.index : quill.getLength() - 1;

    const aggregationOps = [
      "sum",
      "count",
      "count_distinct",
      "min",
      "max",
      "avg",
      "median",
    ];
    const conditionalKeywords = ["IF", "THEN", "ELSE", "END"];
    const logicalKeywords = ["AND", "OR", "NOT"];
    const functionKeywords = ["IN", "ISNULL"];
    const comparisonOps = ["=", "!=", ">", "<", ">=", "<="];

    if (aggregationOps.includes(operator)) {
      const label = getOperatorLabel(operator);
      const labelWithoutBrackets = label
        .replace(/\s*\(\)$/, "")
        .replace(/\s*\((\w+)\)$/, " $1")
        .trim();

      quill.insertEmbed(index, "formula-operator", {
        operator: operator,
        label: labelWithoutBrackets,
      });
      quill.insertText(index + 1, " (");
      quill.insertText(index + 3, ")");
      quill.insertText(index + 4, " ");

      lastCursorPosition = index + 3;
      quill.setSelection(index + 3, 0);
    } else if (conditionalKeywords.includes(operator)) {
      quill.insertEmbed(index, "formula-keyword", {
        keyword: operator,
      });
      quill.insertText(index + 1, " ");
      quill.setSelection(index + 2, 0);
    } else if (logicalKeywords.includes(operator)) {
      quill.insertEmbed(index, "formula-keyword", {
        keyword: operator,
      });
      quill.insertText(index + 1, " ");
      quill.setSelection(index + 2, 0);
    } else if (functionKeywords.includes(operator)) {
      quill.insertEmbed(index, "formula-keyword", {
        keyword: operator,
      });
      quill.insertText(index + 1, " (");
      quill.insertText(index + 3, ")");
      quill.insertText(index + 4, " ");

      lastCursorPosition = index + 3;
      quill.setSelection(index + 3, 0);
    } else if (comparisonOps.includes(operator)) {
      quill.insertEmbed(index, "formula-comparison", {
        operator: operator,
      });
      quill.insertText(index + 1, " ");
      quill.setSelection(index + 2, 0);
    } else {
      quill.insertEmbed(index, "formula-operator", {
        operator: operator,
        label: getOperatorLabel(operator),
      });
      quill.insertText(index + 1, " ");
      quill.setSelection(index + 2, 0);
    }

    isInternalChange = false;
    updateEmptyState();
    emitFormula();
  } catch (error) {
    console.error("An error occurred while inserting operator:", error);
    isInternalChange = false;
  }
};

onMounted(() => {
  try {
    initQuill();
  } catch (error) {
    console.error("An error occurred during component mount:", error);
  }
});

onUnmounted(() => {
  try {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
    quill = null;
  } catch (error) {
    console.error("An error occurred during component unmount:", error);
  }
});

watch(
  () => props.modelValue,
  (newValue, oldValue) => {
    try {
      if (!quill || isInternalChange) {
        return;
      }

      const currentFormula = buildFormulaString(quill);
      const valueChanged = newValue !== currentFormula;

      if (newValue && valueChanged) {
        nextTick(() => {
          renderFormulaToEditor();
        });
      } else if (!newValue && !isEmpty.value) {
        isInternalChange = true;
        quill.deleteText(0, quill.getLength());
        updateEmptyState();
        nextTick(() => {
          isInternalChange = false;
        });
      }
    } catch (error) {
      console.error("An error occurred while watching model value:", error);
      isInternalChange = false;
    }
  },
);

watch(
  () => props.numberAttributes,
  () => {
    try {
      if (props.modelValue) {
        nextTick(() => {
          renderFormulaToEditor();
        });
      }
    } catch (error) {
      console.error(
        "An error occurred while watching number attributes:",
        error,
      );
    }
  },
  { deep: true },
);

const insertString = (value) => {
  try {
    if (!quill) return;

    isInternalChange = true;
    quill.focus();

    const selection = quill.getSelection();
    const index =
      lastCursorPosition !== null
        ? lastCursorPosition
        : selection
          ? selection.index
          : quill.getLength() - 1;
    lastCursorPosition = null;

    quill.insertEmbed(index, "formula-string", {
      value: value,
    });

    quill.insertText(index + 1, " ");
    quill.setSelection(index + 2, 0);

    isInternalChange = false;
    updateEmptyState();
    emitFormula();
  } catch (error) {
    console.error("An error occurred while inserting string:", error);
    isInternalChange = false;
  }
};

defineExpose({
  insertProperty,
  insertOperator,
  insertString,
  getQuill: () => quill,
});
</script>

<style scoped>
.formula_input {
  min-height: 80px;
  max-height: 200px;
  width: 100%;
  border: 1px solid var(--quill-editor-border-color);
  border-radius: 8px;
  background: var(--white-opacity-4);
  cursor: text;
  overflow-y: auto;
}

.formula_input:focus-within {
  border-color: var(--white-opacity-20);
}

.formula_input_empty::before {
  content: attr(data-placeholder);
  color: var(--white-opacity-20);
  pointer-events: none;
  position: absolute;
  padding: 8px 12px;
  font-family: "Proxima Nova", sans-serif;
  font-size: 14px;
}

.formula_input :deep(.ql-editor) {
  padding: 8px 12px;
  min-height: 150px;
  font-family: "Space Mono", monospace;
  font-size: 14px;
  line-height: 2;
}

.formula_input :deep(.ql-editor.ql-blank::before) {
  display: none;
}

.formula_input :deep(.formula_operator) {
  display: inline;
  font-family: "Space Mono", monospace;
  font-size: 14px;
  color: var(--foreground-rgb);
  user-select: none;
  cursor: pointer;
}

.formula_input :deep(.formula_property) {
  display: inline-flex;
  padding: 3px 6px;
  align-items: center;
  border-radius: 16px;
  border: 1px solid var(--white-opacity-8);
  background: var(--white-opacity-14);
  color: var(--foreground-rgb);
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px;
  user-select: none;
  cursor: pointer;
  position: relative;
  top: -2px;
}

.formula_input :deep(.formula_property:hover) {
  background: var(--white-opacity-20);
}

.formula_input :deep(.formula_keyword),
.formula_input :deep(.formula_comparison),
.formula_input :deep(.formula_string) {
  display: inline;
  font-family: "Space Mono", monospace;
  font-size: 14px;
  font-weight: 600;
  user-select: none;
  cursor: pointer;
}

.formula_input :deep(.formula_keyword) {
  color: #c586c0;
}

.formula_input :deep(.formula_comparison) {
  color: #d4d4d4;
}

.formula_input :deep(.formula_string) {
  color: #ce9178;
}
</style>
